/*
 * This file is part of the Det Norske Teatret Nettside 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, {
  forwardRef,
  useRef,
  useImperativeHandle,
  type ComponentProps,
  type RefObject,
} from 'react';
import Picture from 'components/Picture/Picture';
import Loader from 'components/Loader/Loader';
import './Poster.scss';

const DEFAULT_ON_PLAY = () => {};

function getDateTimeDuration(duration: string) {
  const suffix = ['s', 'm', 'h'];
  return duration
    .split(':')
    .reverse()
    .map(time => parseInt(time, 10))
    .map((time, i) => ({
      time,
      suffix: suffix[i],
    }))
    .filter(({ time }) => time > 0)
    .map(({ time, suffix }) => `${time}${suffix}`)
    .join(' ');
}

export interface PosterProps {
  loading?: boolean;
  picture?: ComponentProps<typeof Picture> | null;
  text?: string;
  duration?: string | null;
  play?: string;
  // Ref to the root element, for NodeTransition. `ref` is the imperative handle.
  rootRef?: RefObject<HTMLElement | null> | null;
  onPlay?: () => void;
}

export interface PosterHandle {
  focus: () => void;
}

/**
 * This is the Poster component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 */
const Poster = forwardRef<PosterHandle, PosterProps>(
  (
    {
      loading = false,
      picture = null,
      text,
      duration = null,
      play = 'Play',
      rootRef = null,
      onPlay = DEFAULT_ON_PLAY,
    },
    ref
  ) => {
    const buttonRef = useRef<HTMLButtonElement>(null);

    useImperativeHandle(ref, () => ({
      focus: () => {
        buttonRef.current!.focus();
      },
    }));

    function renderPicture() {
      if (!picture) return null;

      return <Picture {...picture} />;
    }

    function renderDuration() {
      if (!duration) return null;
      return <time dateTime={getDateTimeDuration(duration)}>{duration}</time>;
    }

    function renderText() {
      if (text || duration) {
        return (
          <div className="text">
            <h2 className="h3">{text}</h2>
            {renderDuration()}
          </div>
        );
      }

      return null;
    }

    function renderButton() {
      if (loading) {
        return (
          <div className="button-play">
            <Loader />
          </div>
        );
      }

      return (
        <button
          ref={buttonRef}
          aria-label={play}
          title={play}
          className="button-play"
          onClick={onPlay}
        >
          <span />
        </button>
      );
    }

    return (
      <div
        ref={rootRef as RefObject<HTMLDivElement | null> | null}
        className="poster"
        onClick={onPlay}
      >
        {renderPicture()}
        <div className="overlay" title={play}>
          <div>
            {renderButton()}
            {renderText()}
          </div>
        </div>
      </div>
    );
  }
);

export default Poster;
